/**
 * Widget entry point.
 *
 * Boots into a shadow root attached to a host element we own, so nothing the
 * merchant's admin styles can reach us and nothing we style leaks out. Exposes
 * a tiny imperative API on `window.SuperChat` for host pages that want to open
 * the panel themselves.
 */
import { render } from 'preact';
import { App } from './components/App.jsx';
import { normalizePosition, resolveConfig } from './config.js';
import { CSS } from './styles.js';

const HOST_ID = 'superchat-widget-root';
const STYLE_ID = 'superchat-widget-style';

/** The class that puts the widget on a side, and the one that hides it. */
export const POSITION_CLASS = { left: 'superchat--left', right: 'superchat--right' };
export const HIDDEN_CLASS = 'superchat--hidden';

/**
 * Where the widget sits, as page-level CSS rather than inline styles.
 *
 * Inline styles would make the corner a boot-time decision that nothing could
 * override afterwards. As classes, a merchant flips the side from their own
 * stylesheet or with one `classList.toggle`, and the offset is a custom
 * property they can retune per breakpoint.
 *
 * The host element lives outside the shadow root, so this has to be in the
 * page. Everything it styles is the container; the widget's own look stays
 * sealed inside the shadow root.
 */
function hostStyleSheet() {
  return `
#${HOST_ID} {
  position: fixed;
  bottom: var(--superchat-offset, 24px);
  z-index: 2147483000;
}
#${HOST_ID}.${POSITION_CLASS.right} { right: var(--superchat-offset, 24px); left: auto; }
#${HOST_ID}.${POSITION_CLASS.left}  { left: var(--superchat-offset, 24px); right: auto; }
#${HOST_ID}.${HIDDEN_CLASS} { display: none !important; }
`;
}

function mountHost(config) {
  const existing = document.getElementById(HOST_ID);
  if (existing) existing.remove();
  document.getElementById(STYLE_ID)?.remove();

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = hostStyleSheet();
  document.head.appendChild(style);

  const host = document.createElement('div');
  host.id = HOST_ID;
  host.setAttribute('data-theme', config.theme);
  host.className = `superchat ${POSITION_CLASS[config.position] ?? POSITION_CLASS.right}`;
  host.style.setProperty(
    '--superchat-offset',
    `${Number.isFinite(config.offset) ? config.offset : 24}px`
  );

  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const shadowStyle = document.createElement('style');
  shadowStyle.textContent = CSS;
  shadow.appendChild(shadowStyle);

  const mountPoint = document.createElement('div');
  shadow.appendChild(mountPoint);

  return { host, mountPoint, style };
}

function boot() {
  let config;
  try {
    config = resolveConfig();
  } catch (err) {
    // A misconfigured snippet is the merchant's problem to fix, not something
    // to surface in their admin. Log once and stay out of the way.
    console.error(err.message);
    return;
  }

  const { host, mountPoint, style } = mountHost(config);

  // The App writes its actions here on mount, so the imperative API below
  // drives the same code paths the UI does.
  const controller = {};

  /** Tells the host page what changed, on both the widget and the window. */
  const announce = (type, detail) => {
    for (const target of [host, window]) {
      target.dispatchEvent(new CustomEvent(type, { detail, bubbles: false }));
    }
  };

  const api = {
    config,
    // Kept current by the App via onStateChange.
    state: { open: false, unread: 0, phase: 'booting', hidden: false, position: config.position },
    open: () => controller.open?.(),
    close: () => controller.close?.(),
    toggle: () => (api.state.open ? api.close() : api.open()),
    send: (body) => controller.send?.({ body }),

    /**
     * Take the whole widget off the page, launcher included, and put it back.
     *
     * A class rather than an unmount: the conversation, the socket and the
     * unread count survive, so a page that hides the widget during checkout
     * and shows it again afterwards does not cost the visitor their thread or
     * make them wait through a second boot.
     */
    show() {
      host.classList.remove(HIDDEN_CLASS);
      api.state = { ...api.state, hidden: false };
      announce('superchat:shown', api.state);
    },
    hide() {
      host.classList.add(HIDDEN_CLASS);
      api.state = { ...api.state, hidden: true };
      announce('superchat:hidden', api.state);
    },
    /** 'left' or 'right'. Anything else is read as the right-hand side. */
    setPosition(position) {
      const side = normalizePosition(position);
      host.classList.remove(POSITION_CLASS.left, POSITION_CLASS.right);
      host.classList.add(POSITION_CLASS[side]);
      api.config.position = side;
      api.state = { ...api.state, position: side };
      // Past tense, deliberately: `superchat:position` is the *command*, and
      // reporting on the same name would feed straight back into the listener
      // for it.
      announce('superchat:positioned', api.state);
    },
    /** Supply the exact top-level URL the browser will not let us read. */
    setTopUrl: (topUrl) => controller.setTopUrl?.(topUrl),
    destroy() {
      stopListening();
      render(null, mountPoint);
      host.remove();
      style.remove();
    },
  };

  /**
   * The event half of the same API.
   *
   * A host page that cannot reach `window.SuperChat` — a tag manager, a
   * framework that boots before us, a page where the script is still loading —
   * can still drive the widget by dispatching an event, and the event fires
   * whether or not the widget has finished booting.
   *
   * Both `window` and the widget element are listened on, so a dispatch aimed
   * at either lands. An event that bubbles from the element would reach the
   * window listener as well, which is why each one is handled at most once.
   */
  const COMMANDS = {
    'superchat:open': () => api.open(),
    'superchat:close': () => api.close(),
    'superchat:toggle': () => api.toggle(),
    'superchat:show': () => api.show(),
    'superchat:hide': () => api.hide(),
    'superchat:destroy': () => api.destroy(),
    // detail: 'left' | 'right' | { position: 'left' }
    'superchat:position': (event) =>
      api.setPosition(event.detail?.position ?? event.detail),
  };

  const handled = new WeakSet();
  const onCommand = (event) => {
    if (handled.has(event)) return;
    handled.add(event);
    COMMANDS[event.type]?.(event);
  };

  for (const type of Object.keys(COMMANDS)) {
    window.addEventListener(type, onCommand);
    host.addEventListener(type, onCommand);
  }
  const stopListening = () => {
    for (const type of Object.keys(COMMANDS)) {
      window.removeEventListener(type, onCommand);
      host.removeEventListener(type, onCommand);
    }
  };

  render(
    <App
      config={config}
      controller={controller}
      onStateChange={(state) => {
        api.state = { ...api.state, ...state };
        announce('superchat:state', api.state);
      }}
    />,
    mountPoint
  );

  // Clicking a push notification focuses the tab and asks the widget to open —
  // see `public/superchat-sw.js`.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'superchat:open') api.open();
    });
  }

  /**
   * The same click, but with no tab to focus.
   *
   * The worker cannot post to a page that does not exist yet, so it opens one
   * carrying this marker instead. Read once and removed from the URL, so a
   * reload or a shared link does not keep re-opening the panel.
   */
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('superchat') === 'open') {
      api.open();
      url.searchParams.delete('superchat');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    // A URL we cannot parse is not worth failing the boot over.
  }

  // Preserve any values the snippet set (publicKey, apiBase) while adding the
  // imperative surface on top.
  window.SuperChat = Object.assign(window.SuperChat ?? {}, api);

  // "The widget is up and can be driven now." A page that dispatches commands
  // before this has nothing listening, so this is the signal to wait for.
  announce('superchat:ready', api.state);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
